import { useCallback, useState } from 'react';

import { ButtonLoadingAnimation, Icons, TextButton } from '@opendoor/bricks-next';
import { Box } from '@opendoor/bricks/core';
import { useRouter } from 'next/router';

import {
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useT } from 'helpers/i18n';

import { gateManualAddressVerification } from '../../components/shared/NewSellerFlowAddressInput';
import { useAddressValidationContext } from '../../helpers/AddressValidationContext';
import AddressInput, { AutoCompletePrediction, SelectedAddress } from './AddressInput';

export type { AutoCompletePrediction };

export type NovoAddressInputProps = {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  onAddressSelect: (address: SelectedAddress) => void;
  onPredictionsChange?: (predictions: AutoCompletePrediction[]) => void;
  product?: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  pageSetPath?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
};

const ManualEntryButton: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  onPress: () => void;
}> = ({ onMouseDown, onPress }) => {
  const { t } = useT();

  return (
    <TextButton
      zIndex={2}
      onMouseDown={onMouseDown}
      label={t('homepage.titles.address', 'Enter address manually instead')}
      analyticsName="cosmos-address-input-manual-entry"
      type="button"
      onPress={onPress}
      width={'100%'}
      variant="primary"
      size="medium"
      px={20}
      paddingTop={10}
      paddingBottom={20}
    />
  );
};

const NovoAddressInput: React.FC<NovoAddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  onAddressSelect,
  onPredictionsChange,
  pageSetPath,
  trackingKeywords,
  partnerReferralId,
  channel,
}) => {
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const router = useRouter();
  const { isPredictionSelectLoading } = useAddressValidationContext();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleManualEntryPress = useCallback(() => {
    gateManualAddressVerification({
      forceRedirect: true,
    }).then((result) => {
      if (result.shouldRedirect && result.destination) {
        router.push({
          pathname: result.destination,
          query: {
            ...(trackingKeywords != null && { trackingKeywords }),
            ...(partnerReferralId != null && { partnerReferralId }),
            ...(pageSetPath != null && { pageSetPath }),
            ...(channel != null && { channel }),
          },
        });
      }
    });
  }, [router, trackingKeywords, partnerReferralId, pageSetPath, channel]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePredictionsChange = (newPredictions: AutoCompletePrediction[]) => {
    setPredictions(newPredictions);
    onPredictionsChange?.(newPredictions);
  };

  const handleSearchTermChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const suffix = !isPredictionSelectLoading ? (
    isFocused ? (
      <Icons.Search color="$contentAccentBrand" />
    ) : (
      <Icons.Search />
    )
  ) : (
    <Box position="relative" px={5}>
      <ButtonLoadingAnimation variant="secondary" size="xsmall" />
    </Box>
  );

  const customAction =
    predictions.length < 5 && searchTerm !== '' && isFocused ? (
      <ManualEntryButton onMouseDown={handleMouseDown} onPress={handleManualEntryPress} />
    ) : undefined;

  return (
    <Box className="od-address-input">
      <Box position={'relative'} zIndex={2} className="cta-input-form">
        <AddressInput
          id={id}
          label="Enter your home address"
          hideLabel={hideLabel}
          placeholder={placeholder}
          disabled={disabled}
          onAddressSelect={onAddressSelect}
          onPredictionsChange={handlePredictionsChange}
          onSearchTermChange={handleSearchTermChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          icon={suffix}
          customAction={customAction}
          listBoxId="cb1-listbox"
          listBoxAriaLabel="addresses"
        />
      </Box>
    </Box>
  );
};

export default NovoAddressInput;
